'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { FormEvent, useState } from 'react';

const initialFormData = {
  organization: '',
  contactName: '',
  email: '',
  phone: '',
  sponsorshipType: '',
  requestedAmount: '',
  projectTitle: '',
  audience: '',
  timing: '',
  description: '',
  additionalInfo: '',
};

type FormDataState = typeof initialFormData;
type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function SponsorshipApplicationForm() {
  const router = useRouter();
  const [formData, setFormData] = useState<FormDataState>(initialFormData);
  const [status, setStatus] = useState<SubmissionStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const updateField = (field: keyof FormDataState, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('/api/sponsorship', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, source: 'Gardian sponsorship application page' }),
      });

      const result = await response.json();

      if (!response.ok) {
        setStatus('error');
        setErrorMessage(result.error || 'Unable to submit application. Please try again.');
        return;
      }

      router.push('/en/community/sponsorship/thank-you');
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong submitting the form. Please try again later.');
      console.error('Sponsorship form submit error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto px-4">
        <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary/80">
              Community sponsorship
            </p>
            <h1 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">
              Apply for FY 2027-2028 Gardian sponsorship
            </h1>
            <p className="mt-4 max-w-3xl text-gray-600">
              FY 2026-2027 applications are closed. Tell us about your event, program or initiative for FY 2027-2028 and we&apos;ll review your request.
            </p>
          </div>
          <Link
            href="/en/community"
            className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-50"
          >
            Back to community
          </Link>
        </div>

        <div className="mx-auto max-w-4xl rounded-3xl bg-white p-8 shadow-sm">
          {status === 'success' && (
            <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 p-4 text-green-900">
              <p className="font-semibold">Your sponsorship application has been submitted.</p>
              <p className="mt-1 text-sm">We will review it and contact you shortly.</p>
            </div>
          )}

          {status === 'error' && errorMessage && (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-900">
              <p className="font-semibold">Submission failed</p>
              <p className="mt-1 text-sm">{errorMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Organisation / group name</span>
                <input
                  type="text"
                  value={formData.organization}
                  onChange={(event) => updateField('organization', event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Contact name</span>
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(event) => updateField('contactName', event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Email address</span>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(event) => updateField('email', event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Phone number</span>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(event) => updateField('phone', event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Sponsorship type</span>
                <select
                  value={formData.sponsorshipType}
                  onChange={(event) => updateField('sponsorshipType', event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                >
                  <option value="">Choose one</option>
                  <option value="Event">Event</option>
                  <option value="Team or club">Team or club</option>
                  <option value="Charity / not-for-profit">Charity / not-for-profit</option>
                  <option value="Community program">Community program</option>
                  <option value="In-kind support">In-kind support</option>
                  <option value="Other">Other</option>
                </select>
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Requested amount</span>
                <input
                  type="text"
                  value={formData.requestedAmount}
                  onChange={(event) => updateField('requestedAmount', event.target.value)}
                  placeholder="e.g. $2,000"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Project, event or program title</span>
                <input
                  type="text"
                  value={formData.projectTitle}
                  onChange={(event) => updateField('projectTitle', event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-slate-700">Audience / reach</span>
                <input
                  type="text"
                  value={formData.audience}
                  onChange={(event) => updateField('audience', event.target.value)}
                  placeholder="e.g. 500 attendees"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
                />
              </label>
            </div>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Timing / event date</span>
              <input
                type="text"
                value={formData.timing}
                onChange={(event) => updateField('timing', event.target.value)}
                placeholder="e.g. June 2026, ongoing program"
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Why Gardian should sponsor this</span>
              <textarea
                value={formData.description}
                onChange={(event) => updateField('description', event.target.value)}
                required
                rows={5}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Additional information</span>
              <textarea
                value={formData.additionalInfo}
                onChange={(event) => updateField('additionalInfo', event.target.value)}
                rows={4}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10"
              />
            </label>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-slate-600">We will email you once the application is received.</div>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-75"
              >
                {status === 'submitting' ? 'Submitting...' : 'Submit application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
